import type {
  MbcThreadMessageDto,
  MbcThreadMessagePart,
} from "@nlc/shared-types";

import type { MBCDataTypes, MBCUIMessage } from "../types/mbc-chat.types";

import { MBC_MASTRA_API_URL } from "../config/mbc-chat.config";

type MBCMessagePart = MBCUIMessage["parts"][number];

// Known `data-*` suffixes this chat can render — mirrors the keys of
// `MBCDataTypes`. A part whose suffix isn't here is dropped rather than cast,
// so an older frontend degrades gracefully against a newer persisted chip.
const KNOWN_DATA_KEYS = new Set<keyof MBCDataTypes>([
  "brandSelect",
  "choices",
  "reviewCta",
  "uploadPrompt",
]);

function toMbcPart(part: MbcThreadMessagePart): MBCMessagePart | null {
  if ("text" in part) return { text: part.text, type: "text" };

  const key = part.type.slice("data-".length);

  if (!KNOWN_DATA_KEYS.has(key as keyof MBCDataTypes)) return null;

  return { data: part.data, type: part.type } as MBCMessagePart;
}

/**
 * Load a thread's transcript from the AI module and map it to chat messages, so
 * reopening a draft rehydrates the conversation instead of showing an empty chat.
 */
export async function fetchThreadMessages(
  threadId: string,
  resourceId?: string,
): Promise<MBCUIMessage[]> {
  const query = resourceId
    ? `?resourceId=${encodeURIComponent(resourceId)}`
    : "";

  const response = await fetch(
    `${MBC_MASTRA_API_URL}/mbc/threads/${encodeURIComponent(threadId)}/messages${query}`,
  );

  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const body = (await response.json()) as { messages: MbcThreadMessageDto[] };

  return body.messages.map((message) => ({
    id: message.id,
    parts: message.parts
      .map((part) => toMbcPart(part))
      .filter((part): part is MBCMessagePart => part !== null),
    role: message.role,
  }));
}
